import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { ScreenBody } from '../components/voiceUi';
import Icon from '../components/icon';
import { fmtDur } from '../utils/format';
import { useLiveCalls } from '../context/liveCallsContext';

// Font scale. This is a normal in-app console page (a sidebar menu item), not
// the fullscreen LED-wall board, so SCALE = 1 keeps text aligned with the other
// pages. The fs() calls remain so sizing stays tunable from one knob if needed.
const SCALE = 1;
const fs = (n: number) => Math.round(n * SCALE);

// Live Calls — real SSE feed (GET /v1/live/calls). Each active call renders from
// the live frames the runtime publishes (caller/status/elapsed). Fields the feed
// doesn't emit (intent/mood/step) are omitted — no mock enrichment. Empty feed =
// truthful "no live calls" state.
export default function LiveCallsScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { openLive, liveTick } = useConsole();
  const { calls } = useLiveCalls();
  // Equalizer bar heights cycle with the tick (approximates the CSS sk-bar anim).
  const eq = (i: number) => 8 + Math.round(8 * Math.abs(Math.sin((liveTick + i) * 1.3)));
  // Elapsed seconds since we first saw the call (liveTick forces a re-render/sec).
  const elapsed = (startedAt: number) => Math.max(0, Math.floor((Date.now() - startedAt) / 1000));

  if (calls.length === 0) {
    return (
      <ScreenBody maxWidth={1000}>
        <View style={{ padding: 40, alignItems: 'center' }}>
          <Text style={{ fontSize: fs(13), color: c.ink4, fontFamily: theme.font.sans }}>No live calls right now.</Text>
        </View>
      </ScreenBody>
    );
  }

  return (
    <ScreenBody maxWidth={1000}>
      <View style={{ gap: 12 }}>
        {calls.map((l) => (
          <View key={l.id} style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, ...(theme.shadows.sm as object) }}>
            <Pressable onPress={() => openLive(l.id, { id: l.id, contact: l.num, dir: 'inbound', outcome: 'completed', durSec: elapsed(l.startedAt) })} style={{ flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, paddingVertical: 14 }}>
              {/* 3-bar equalizer (static bars; animation added later via Animated) */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, height: 26 }}>
                {[0, 1, 2].map(i => (
                  <View key={i} style={{ width: 3, height: eq(i), backgroundColor: c.ink, borderRadius: 2 }} />
                ))}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 3 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: fs(13), fontWeight: '600', color: c.ink }}>{l.num}</Text>
                </View>
                <Text style={{ fontSize: fs(12.5), color: c.ink2, fontFamily: theme.font.sans }}>{l.status}</Text>
              </View>
              <Text style={{ fontFamily: theme.font.mono, fontSize: fs(13), color: c.ink3, width: fs(44), textAlign: 'right' }}>{fmtDur(elapsed(l.startedAt))}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Text style={{ fontSize: fs(11.5), color: c.ink3, fontFamily: theme.font.sans }}>Open transcript</Text>
                <Icon name="chev-r" size={fs(14)} color={c.ink3} />
              </View>
            </Pressable>
          </View>
        ))}
      </View>
    </ScreenBody>
  );
}
